/**
 * Access level of an API key, as chosen in the create dialog and shown on the key card.
 * `read` is the backend's read-only preset (every `*:read` scope, no write); `full` is the
 * wildcard. A key whose scopes are neither is `custom` (minted through the API directly).
 */
export type ApiKeyAccess = 'full' | 'read';
export type ApiKeyAccessLabel = ApiKeyAccess | 'custom';

/**
 * Mirrors `READ_ONLY_SCOPES` in whatsapp-scheduler-backend
 * (src/services/internal/api-keys/mintable-scopes.ts). The backend validates the set and
 * rejects unknown scopes, so a stale entry fails loudly (400) rather than widening a key.
 */
export const READ_ONLY_API_KEY_SCOPES: readonly string[] = [
  'account:read',
  'audiences:read',
  'boards:read',
  'campaigns:read',
  'chats:read',
  'contacts:read',
  'engines:read',
  'gmail:read',
  'groups:read',
  'messages:read',
  'newsletters:read',
  'suno:read',
  'wa-labels:read',
  'webhooks:read',
];

/** Scopes to send on create. `full` sends the wildcard explicitly. */
export function scopesForAccess(access: ApiKeyAccess): string[] {
  return access === 'read' ? [...READ_ONLY_API_KEY_SCOPES] : ['*'];
}

/** What a key's stored scopes amount to, for the key card. */
export function accessOfScopes(scopes: readonly string[] | undefined): ApiKeyAccessLabel {
  if (!scopes || scopes.length === 0 || scopes.includes('*')) return 'full';
  return scopes.every((s) => s.endsWith(':read')) ? 'read' : 'custom';
}
